import React, { useState, useEffect } from "react";
import { BlogPost } from "../types/blog";
import { 
  substackSync, 
  SubstackSyncConfig, 
  SubstackSyncResult,
  needsSync,
  formatSyncStatus
} from "../utils/substackSync";
import { SyncStatus } from "../utils/substackApi";
import LoadingSpinner from "./LoadingSpinner";

interface SubstackSyncProps {
  posts: BlogPost[];
  onClose?: () => void;
}

const SubstackSync: React.FC<SubstackSyncProps> = ({ posts, onClose }) => {
  const [config, setConfig] = useState<SubstackSyncConfig>({
    publicationUrl: 'arvindkc.substack.com',
    autoSyncEnabled: false,
    syncMethod: 'manual'
  });
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SubstackSyncResult | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [manualSyncInstructions, setManualSyncInstructions] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load configuration and sync statuses in parallel
      const [configData, statusData] = await Promise.all([
        substackSync.getSyncConfig(),
        substackSync.getSyncStatuses()
      ]);

      if (configData) {
        setConfig(configData);
        await substackSync.initialize(configData);
      } else {
        setShowConfig(true); // Show config form if not configured
      }

      setSyncStatuses(statusData);
    } catch (err) {
      console.error('Error loading sync data:', err);
      setError('Failed to load sync data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setIsConfiguring(true);
      setError(null);

      if (!config.publicationUrl) {
        setError('Publication URL is required');
        return;
      }

      if (!config.publicationUrl.includes('substack.com')) {
        setError('Please enter a valid Substack URL (e.g., yourname.substack.com)');
        return;
      }

      const success = await substackSync.saveSyncConfig(config);
      if (success) {
        setShowConfig(false);
        await loadData(); // Reload data
      } else {
        setError('Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Failed to save configuration');
    } finally {
      setIsConfiguring(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setIsSyncing(true);
      setSyncResult(null);
      setError(null);

      const result = await substackSync.syncAllPosts(posts);
      setSyncResult(result);

      if (!result.success && result.errors.length > 0) {
        setError(result.errors.join('; '));
      }

      // Reload sync statuses to show updated data
      const updatedStatuses = await substackSync.getSyncStatuses();
      setSyncStatuses(updatedStatuses);
    } catch (err) {
      console.error('Error during sync:', err);
      setError('Failed to sync posts');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncSelected = async () => {
    try {
      setIsSyncing(true);
      setError(null);

      const postsToSync = posts.filter(post => selectedPosts.has(post.id));
      const result = await substackSync.syncAllPosts(postsToSync);
      setSyncResult(result);

      // Reload sync statuses
      const updatedStatuses = await substackSync.getSyncStatuses();
      setSyncStatuses(updatedStatuses);
      
      // Clear selection
      setSelectedPosts(new Set());
    } catch (err) {
      console.error('Error syncing selected posts:', err);
      setError('Failed to sync selected posts');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleGenerateInstructions = (post: BlogPost) => {
    const instructions = substackSync.generateManualSyncInstructions(post);
    setManualSyncInstructions(instructions);
  };

  const togglePostSelection = (postId: string) => {
    const newSelection = new Set(selectedPosts);
    if (newSelection.has(postId)) {
      newSelection.delete(postId);
    } else {
      newSelection.add(postId);
    }
    setSelectedPosts(newSelection);
  };

  const selectAllUnsyncedPosts = () => {
    const unsyncedPosts = posts.filter(post => needsSync(post, syncStatuses));
    const newSelection = new Set(unsyncedPosts.map(post => post.id));
    setSelectedPosts(newSelection);
  };

  const getSyncStatusForPost = (postId: string): SyncStatus | undefined => {
    return syncStatuses.find(s => s.postId === postId);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <LoadingSpinner message="Loading Substack sync data..." />
      </div>
    );
  }

  if (showConfig) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Configure Substack Sync
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Substack Publication URL
            </label>
            <input
              type="text"
              value={config.publicationUrl}
              onChange={(e) => setConfig({ ...config, publicationUrl: e.target.value })}
              placeholder="arvindkc.substack.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Your Substack publication URL: arvindkc.substack.com
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sync Method
            </label>
            <select
              value={config.syncMethod}
              onChange={(e) => setConfig({ ...config, syncMethod: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="manual">Manual (Copy/Paste Instructions)</option>
              <option value="email">Email Publishing (Coming Soon)</option>
              <option value="api">API Integration (Coming Soon)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Manual method provides copy/paste instructions for now
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoSync"
              checked={config.autoSyncEnabled}
              onChange={(e) => setConfig({ ...config, autoSyncEnabled: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="autoSync" className="ml-2 text-sm text-gray-700">
              Enable auto-sync on post publish
            </label>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleSaveConfig}
            disabled={isConfiguring}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isConfiguring ? 'Saving...' : 'Save Configuration'}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  const unsyncedPosts = posts.filter(post => needsSync(post, syncStatuses));
  const syncedPosts = posts.filter(post => !needsSync(post, syncStatuses) && !post.isDraft);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Substack Sync</h2>
          <p className="text-sm text-gray-600 mt-1">
            Publication: {config.publicationUrl} | 
            Auto-sync: {config.autoSyncEnabled ? 'Enabled' : 'Disabled'} |
            Method: {config.syncMethod}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowConfig(true)}
            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Settings
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {syncResult && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          <p>Sync completed: {syncResult.syncedPosts} synced, {syncResult.failedPosts} failed</p>
          {syncResult.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer">View errors</summary>
              <ul className="list-disc list-inside mt-1 text-sm">
                {syncResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Sync Overview</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Total Posts:</span>
              <span>{posts.filter(p => !p.isDraft).length}</span>
            </div>
            <div className="flex justify-between">
              <span>Synced:</span>
              <span className="text-green-600">{syncedPosts.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Need Sync:</span>
              <span className="text-orange-600">{unsyncedPosts.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Drafts:</span>
              <span className="text-gray-500">{posts.filter(p => p.isDraft).length}</span>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <button
              onClick={handleSyncAll}
              disabled={isSyncing || unsyncedPosts.length === 0}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSyncing ? 'Syncing...' : `Sync All (${unsyncedPosts.length})`}
            </button>
            
            <button
              onClick={selectAllUnsyncedPosts}
              disabled={unsyncedPosts.length === 0}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Select All Unsynced
            </button>

            {selectedPosts.size > 0 && (
              <button
                onClick={handleSyncSelected}
                disabled={isSyncing}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {isSyncing ? 'Syncing...' : `Sync Selected (${selectedPosts.size})`}
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Posts Status</h3>
          
          <div className="overflow-x-auto max-h-96 border rounded-lg">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selectedPosts.size > 0 && selectedPosts.size === unsyncedPosts.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllUnsyncedPosts();
                        } else {
                          setSelectedPosts(new Set());
                        }
                      }}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500">Title</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.filter(p => !p.isDraft).map((post) => {
                  const syncStatus = getSyncStatusForPost(post.id);
                  const needsSyncCheck = needsSync(post, syncStatuses);
                  
                  return (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedPosts.has(post.id)}
                          onChange={() => togglePostSelection(post.id)}
                          disabled={!needsSyncCheck}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {post.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(post.date).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm">
                          {syncStatus ? formatSyncStatus(syncStatus) : '⚪ Not synced'}
                          {syncStatus?.substackUrl && (
                            <a
                              href={syncStatus.substackUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {config.syncMethod === 'manual' && needsSyncCheck && (
                          <button
                            onClick={() => handleGenerateInstructions(post)}
                            className="text-sm text-indigo-600 hover:text-indigo-800"
                          >
                            Instructions
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {manualSyncInstructions && (
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Manual Sync Instructions</h4>
          <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-auto max-h-40">
            {manualSyncInstructions}
          </pre>
          <button
            onClick={() => navigator.clipboard.writeText(manualSyncInstructions)}
            className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
          >
            Copy to Clipboard
          </button>
          <button
            onClick={() => setManualSyncInstructions('')}
            className="mt-2 ml-4 text-sm text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default SubstackSync;